/* RIZIKA SMLOUVY PO OSOBÁCH — JEDNA PRAVDA O TOM, KOHO A NA CO SMLOUVA POJIŠŤUJE.
 * ============================================================================
 * Čistá funkce bez Reactu. Čte ji analýza (tabulka rizik u pojistky, průvodce),
 * srovnání Před a po, Návrh řešení i plán (lib/srovnaniPojisteni.js →
 * rusenePojistky, ztraceneKryti) — nikdo jiný si limity z řádku nevykládá sám.
 *
 * PROČ (majitel 14. 10. 2026)
 * „proč tam je koho pojišťuje.. stačí tam zadat, když na to klikne, doplní se
 *  tam rizika, co klient má ve smlouvě muž žena.. aby se dalo porovnat… ve
 *  výstupu — invalidita, smrt, pracovní neschopnost, závažné onemocnění, trvalé
 *  následky úrazem, denní odškodné úrazem. U pojištění odpovědnosti nejde
 *  zadávat invalidita a smrt a další rizika.“
 *
 * Dřív měla smlouva JEDNU sadu limitů a výběr „Koho pojišťuje“ (klient /
 * partner). Společnou smlouvu muže a ženy tak nešlo zapsat vůbec — a poradce
 * musel vybírat osobu v bublině ⋯, kterou nikdo nenašel.
 *
 * NOVÝ TVAR (zpětně kompatibilní)
 *   pojistka.rizikaOsob = {
 *     k1: { limitInvalidita, limitSmrt, limitPn, limitZavazne, limitTnu, limitOdskodne },
 *     k2: { … },
 *   }
 * Kdo má vyplněné aspoň jedno riziko, je ve smlouvě pojištěný. Výběr „Koho
 * pojišťuje“ tím odpadá — `osoba` se dál ukládá, ale jen ODVOZENĚ (jedna
 * osoba → její klíč, společná smlouva nebo žádná → prázdno), aby starší čtenáři
 * (podklady pro klienta, náhrada majetku) dostali totéž co dřív.
 * Platba zůstává na smlouvě (jedna platba za celou smlouvu).
 *
 * STARÝ TVAR SE ČTE BEZE ZMĚNY ČÍSEL
 *   `osoba` + ploché `limitSmrt`, `limitInvalidita`, `limitZavazne`, `limitPn`,
 *   `limitOdskodne`, `limitTnu`. Řádek bez `rizikaOsob` (každá uložená analýza)
 *   vrací přesně tytéž limity a tutéž osobu jako dřív; nepřiřazená smlouva
 *   zůstává nepřiřazená (`bezOsoby`) — u jednoho člověka ji výpočty dál dávají
 *   jemu, u dvou živiteli (srovnání), jak tomu bylo. Staré plány se proto
 *   nepřepočítávají (razítko metodiky nic nového nepotřebuje).
 *   Převod na nový tvar nastane AŽ zápisem poradce do tabulky rizik
 *   (`patchRiziko`, `patchPriradit`) — ploché limity se přesunou, nic se neztratí.
 *
 * NEZNÁMÉ ≠ 0
 *   Prázdné pole = `null` (nevíme). Výslovná nula = údaj („na tohle riziko
 *   pojištěný není“) — `asCislo`, totéž pravidlo jako dřív `maLimit`.
 */
import { asCislo, asObj, asText } from "../bezpecneHodnoty";

/** Klíče osob, které smlouva zná (klient, partner). Děti dnešní model nemá. */
export const KLICE_OSOB_SMLOUVY = Object.freeze(["k1", "k2"]);

/** Riziko → klíč limitu (starý plochý tvar i hodnoty v `rizikaOsob[k]`). */
export const KLIC_LIMITU_RIZIKA = Object.freeze({
  invalidita: "limitInvalidita",
  smrt: "limitSmrt",
  zavazne: "limitZavazne",
  pn: "limitPn",
  odskodne: "limitOdskodne",
  tnu: "limitTnu",
});
export const KLICE_LIMITU_SMLOUVY = Object.freeze(Object.values(KLIC_LIMITU_RIZIKA));
const RIZIKA = Object.keys(KLIC_LIMITU_RIZIKA);

/** Pořadí řádků tabulky rizik v analýze (majitel 14. 10. 2026, v pořadí, jak
 *  je diktoval): invalidita, smrt, pracovní neschopnost, závažné onemocnění,
 *  trvalé následky úrazem, denní odškodné úrazem. Před a po má dál pořadí
 *  karty Pojištění osob (lib/poradiRizik.js). */
export const RIZIKA_ZADANI = Object.freeze(["invalidita", "smrt", "pn", "zavazne", "tnu", "odskodne"]);

/** Denní dávky (Kč/den); ostatní rizika jsou jednorázová pojistná částka. */
export const DENNI_RIZIKA = Object.freeze(["pn", "odskodne"]);

/* Druhy pojistek, které NEJSOU pojištěním osoby — tabulka rizik osob se u nich
 * nenabízí (odpovědnost, majetek, povinné ručení, právní ochrana, auto, cesty,
 * podnikání). Totéž, co srovnání pojištění bere jako „ne pojištění osoby“
 * (lib/srovnaniPojisteni.js čte odsud). */
export const DRUHY_BEZ_RIZIK_OSOB = Object.freeze([
  "nemovitost", "domacnost", "odpovednost", "pravni_ochrana", "cestovni",
  "povinne_ruceni", "havarijni", "podnikatelske", "zamestnavatelske",
]);

/** Je druh smlouvy bez rizik osob (odpovědnost, majetek…)? Bez druhu = ne. */
export function druhBezRizikOsob(row) {
  return DRUHY_BEZ_RIZIK_OSOB.includes(asText(asObj(row).druh).trim());
}

/* „Neznámé ≠ 0“ — `asCislo` propouští pole (`Number([]) === 0`), což by
 * z rozbitého uloženého pole udělalo platnou nulu. */
const cislo = (v) => (Array.isArray(v) ? null : asCislo(v));

const prazdneLimity = () => Object.fromEntries(RIZIKA.map((r) => [r, null]));
const limityZ = (o) => {
  const obj = asObj(o);
  return Object.fromEntries(RIZIKA.map((r) => [r, cislo(obj[KLIC_LIMITU_RIZIKA[r]])]));
};
const maLimity = (l) => RIZIKA.some((r) => l[r] != null);

/** Má řádek nový tvar (`rizikaOsob` jako objekt)? Pak je jediným zdrojem pravdy. */
export function jeNovyTvar(row) {
  const ro = asObj(row).rizikaOsob;
  return !!ro && typeof ro === "object" && !Array.isArray(ro);
}

/**
 * RIZIKA SMLOUVY — normalizace obou tvarů.
 * @param {object} row řádek `d.pojistky[]`
 * @returns {{
 *   novyTvar: boolean,
 *   osoby: Array<{ klic: "k1"|"k2", index: 0|1, limity: Record<string, number|null> }>,
 *   bezOsoby: Record<string, number|null> | null,
 *   spolecna: boolean,
 *   maRizika: boolean,
 * }}
 *   `osoby` = kdo je ve smlouvě pojištěný (nový tvar: kdo má aspoň jedno
 *   riziko; starý tvar: vybraná `osoba`, i bez limitu — přiřazení platí dál),
 *   `bezOsoby` = starý zápis limitů bez vybrané osoby (nevíme čí),
 *   `spolecna` = smlouva pojišťuje oba (jen nový tvar),
 *   `maRizika` = je zapsané aspoň jedno riziko (i výslovná nula).
 */
export function rizikaSmlouvy(row) {
  const r = asObj(row);
  if (jeNovyTvar(r)) {
    const ro = r.rizikaOsob;
    const osoby = KLICE_OSOB_SMLOUVY
      .map((klic, index) => ({ klic, index, limity: limityZ(ro[klic]) }))
      .filter((o) => maLimity(o.limity));
    return { novyTvar: true, osoby, bezOsoby: null, spolecna: osoby.length > 1, maRizika: osoby.length > 0 };
  }
  const limity = limityZ(r);
  const i = KLICE_OSOB_SMLOUVY.indexOf(asText(r.osoba).trim());
  const ma = maLimity(limity);
  if (i !== -1) {
    return { novyTvar: false, osoby: [{ klic: KLICE_OSOB_SMLOUVY[i], index: i, limity }], bezOsoby: null, spolecna: false, maRizika: ma };
  }
  return { novyTvar: false, osoby: [], bezOsoby: ma ? limity : null, spolecna: false, maRizika: ma };
}

/**
 * ČÁSTI SMLOUVY PRO VÝPOČTY — jedna na pojištěnou osobu.
 * Starý tvar dá vždy PRÁVĚ JEDNU část (osoba nebo `null`, limity z řádku) —
 * přesně to, co srovnání čte odjakživa. Nový tvar: část za každou osobu
 * s rizikem (společná smlouva = dvě části, `spolecna: true`); smlouva bez
 * jediného rizika = jedna nepřiřazená část bez limitů (jen platba).
 * @returns {Array<{ osoba: 0|1|null, limity: Record<string, number|null>, spolecna: boolean }>}
 */
export function castiSmlouvy(row) {
  const rs = rizikaSmlouvy(row);
  if (!rs.novyTvar) {
    const o = rs.osoby[0];
    return [{ osoba: o ? o.index : null, limity: o ? o.limity : limityZ(row), spolecna: false }];
  }
  if (!rs.osoby.length) return [{ osoba: null, limity: prazdneLimity(), spolecna: false }];
  return rs.osoby.map((o) => ({ osoba: o.index, limity: o.limity, spolecna: rs.spolecna }));
}

/** Indexy pojištěných osob smlouvy (prázdné = nevíme / nikdo). */
export function osobySmlouvy(row) {
  return rizikaSmlouvy(row).osoby.map((o) => o.index);
}

/** Nejvyšší známý limit rizika napříč osobami a starým zápisem — `null` = nikde. */
export function limitRizikaSmlouvy(row, riziko) {
  const rs = rizikaSmlouvy(row);
  const vse = [...rs.osoby.map((o) => o.limity[riziko]), rs.bezOsoby ? rs.bezOsoby[riziko] : null]
    .filter((x) => x != null);
  return vse.length ? Math.max(...vse) : null;
}

/** Má smlouva u některého z rizik (výchozí: všech) KLADNÝ limit? */
export function maKladnyLimit(row, rizika = RIZIKA) {
  return rizika.some((riz) => {
    const n = limitRizikaSmlouvy(row, riz);
    return n != null && n > 0;
  });
}

/** Součet limitu rizika za celou smlouvu (všechny osoby + starý zápis), `null` = nic. */
export function soucetRizikaSmlouvy(row, riziko) {
  const rs = rizikaSmlouvy(row);
  const vse = [...rs.osoby.map((o) => o.limity[riziko]), rs.bezOsoby ? rs.bezOsoby[riziko] : null]
    .filter((x) => x != null);
  return vse.length ? vse.reduce((a, b) => a + b, 0) : null;
}

/**
 * Je v `rizikaOsob` zapsaný aspoň jeden údaj (i výslovná nula)? Pro „řádek
 * s údajem“ (model.js) a pro šablonový řádek, který se nesmí vypustit.
 */
export function rizikaOsobMajiUdaj(v) {
  if (!v || typeof v !== "object" || Array.isArray(v)) return false;
  return KLICE_OSOB_SMLOUVY.some((k) => maLimity(limityZ(v[k])));
}

/* ── ZÁPIS (převod starého tvaru na nový) ─────────────────────────────── */

/** Odvozená `osoba` z rizik: jedna osoba → její klíč, jinak prázdno. */
export function osobaZRizik(rizikaOsob) {
  const s = KLICE_OSOB_SMLOUVY.filter((k) => maLimity(limityZ(asObj(rizikaOsob)[k])));
  return s.length === 1 ? s[0] : "";
}

const surovePloche = (r) => Object.fromEntries(KLICE_LIMITU_SMLOUVY.map((k) => [k, r[k] === undefined || r[k] === null ? "" : r[k]]));
const prazdnePloche = () => Object.fromEntries(KLICE_LIMITU_SMLOUVY.map((k) => [k, ""]));

/**
 * Rizika osob ve tvaru pro úpravu `{ k1: {limit…}, k2: {…} }`. Starý zápis se
 * převede se SUROVÝMI hodnotami (žádné přepočítání čísel). Nepřiřazené staré
 * limity dostane klient jen tehdy, když je v analýze sám (`jedinaOsoba`) —
 * u dvou lidí zůstanou v `bezOsoby`, dokud je poradce nepřiřadí.
 */
export function rizikaOsobProUpravu(row, { jedinaOsoba = false } = {}) {
  const r = asObj(row);
  if (jeNovyTvar(r)) {
    const out = {};
    KLICE_OSOB_SMLOUVY.forEach((k) => {
      const o = r.rizikaOsob[k];
      if (o && typeof o === "object" && !Array.isArray(o)) out[k] = { ...o };
    });
    return out;
  }
  const out = {};
  const i = KLICE_OSOB_SMLOUVY.indexOf(asText(r.osoba).trim());
  if (i !== -1) out[KLICE_OSOB_SMLOUVY[i]] = surovePloche(r);
  else if (jedinaOsoba && maLimity(limityZ(r))) out.k1 = surovePloche(r);
  return out;
}

/**
 * Patch zápisu jednoho rizika jedné osobě (pro `onRow(id, patch)`).
 * Starý tvar se při prvním zápisu převede: ploché limity se přesunou do
 * `rizikaOsob`, ploché klíče se vyprázdní, `osoba` se odvodí.
 * U dvou lidí s nepřiřazeným starým zápisem vrací `null` — nejdřív
 * `patchPriradit`, jinak by se starý zápis ztratil.
 * @returns {object|null}
 */
export function patchRiziko(row, klicOsoby, riziko, hodnota, { jedinaOsoba = false } = {}) {
  const klic = KLIC_LIMITU_RIZIKA[riziko];
  if (!klic || !KLICE_OSOB_SMLOUVY.includes(klicOsoby)) return null;
  const rs = rizikaSmlouvy(row);
  if (!rs.novyTvar && rs.bezOsoby && !jedinaOsoba) return null;
  const ro = rizikaOsobProUpravu(row, { jedinaOsoba });
  const nove = { ...ro, [klicOsoby]: { ...prazdnePloche(), ...asObj(ro[klicOsoby]), [klic]: hodnota } };
  return { rizikaOsob: nove, ...prazdnePloche(), osoba: osobaZRizik(nove) };
}

/**
 * Starý nepřiřazený zápis limitů → osobě `klicOsoby` (jedno kliknutí
 * „patří Janovi“). Čísla se přesunou beze změny. Řádek bez starého
 * nepřiřazeného zápisu vrací `null`.
 */
export function patchPriradit(row, klicOsoby) {
  if (!KLICE_OSOB_SMLOUVY.includes(klicOsoby)) return null;
  const r = asObj(row);
  const rs = rizikaSmlouvy(r);
  if (rs.novyTvar || !rs.bezOsoby) return null;
  const nove = { [klicOsoby]: surovePloche(r) };
  return { rizikaOsob: nove, ...prazdnePloche(), osoba: osobaZRizik(nove) };
}

/**
 * ČÁSTKA RIZIKA Z TEXTU POLE. Pole má `inputmode="decimal"` (telefon nabídne
 * čárku) a denní dávka v eurech bývá s desetinami (12,50 €). Dřív se zahodilo
 * vše kromě číslic a „12,50“ se tiše uložilo jako 1 250 (kontrola 14. 10.
 * 2026, B5). Teď:
 *   · mezery (i nezlomitelné) = tisíce („1 000 000“),
 *   · „.“ nebo „,“ před trojicemi číslic = tisíce („1.000“, „1,000,000“),
 *   · poslední „.“ / „,“ s nejvýš dvěma číslicemi = desetiny („12,50“, „12,“),
 *   · prázdno = "" (nevíme, ne 0); výslovná nula = 0,
 *   · cokoli jiného = `undefined` — nečitelné, nic se nezapíše.
 * Zaokrouhluje na setiny (haléře, centy).
 */
export function castkaZTextu(text) {
  const s = String(text ?? "").replace(/\s/g, "");
  if (s === "") return "";
  const m = /^(\d+|\d{1,3}(?:([.,])\d{3})+)(?:([.,])(\d{0,2}))?$/.exec(s);
  if (!m) return undefined;
  const [, cela, tisice, desetinna, desetiny] = m;
  /* „1.000.5“ — týž znak jako tisíce i desetiny: dvojznačné, nezapisovat. */
  if (tisice && desetinna === tisice) return undefined;
  const n = Number(`${cela.replace(/[.,]/g, "")}.${desetiny || "0"}`);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : undefined;
}

/* ── KONTROLA „POJISTKA BEZ OSOBY“ ────────────────────────────────────── */

/* Texty v pěti jazycích (průvodce i řádek tabulky). */
const TEXTY = {
  priradit: {
    cs: ["Komu patří rizika?", "Starší zápis: rizika nejsou přiřazená osobě. Pod tabulkou v části Rizika ve smlouvách vyberte, komu patří."],
    sk: ["Komu patria riziká?", "Starší zápis: riziká nie sú priradené osobe. Pod tabuľkou v časti Riziká v zmluvách vyberte, komu patria."],
    en: ["Whose risks are these?", "Older entry: the risks are not assigned to a person. Below the table, under Risks in contracts, pick whose they are."],
    uk: ["Чиї це ризики?", "Старіший запис: ризики не прив'язані до особи. Під таблицею в розділі Ризики в договорах виберіть, чиї вони."],
    vi: ["Rủi ro này của ai?", "Mục nhập cũ: rủi ro chưa gắn với người nào. Bên dưới bảng, trong phần Rủi ro trong hợp đồng, hãy chọn của ai."],
  },
  bezRizik: {
    cs: ["Doplňte rizika", "Smlouva nemá zapsané žádné riziko, takže nevíme, koho pojišťuje. Doplňte rizika pod tabulkou."],
    sk: ["Doplňte riziká", "Zmluva nemá zapísané žiadne riziko, takže nevieme, koho poisťuje. Doplňte riziká pod tabuľkou."],
    en: ["Add the risks", "No risk is entered for this contract, so we do not know who it insures. Add the risks below the table."],
    uk: ["Додайте ризики", "У договорі не записано жодного ризику, тож невідомо, кого він страхує. Додайте ризики під таблицею."],
    vi: ["Bổ sung rủi ro", "Hợp đồng chưa ghi rủi ro nào nên chưa biết bảo hiểm cho ai. Hãy bổ sung rủi ro bên dưới bảng."],
  },
};

const textKontroly = (kod, lang) => (TEXTY[kod][lang] || TEXTY[kod].cs);

/** Je řádek skutečná smlouva (ne nedotčený předvyplněný řádek)? */
const vyplnenaSmlouva = (r) => {
  const n = cislo(r.platba);
  return (n != null && n > 0) || asText(r.instituce).trim() !== "" || asText(r.cislo).trim() !== "";
};

/**
 * KONTROLA „POJISTKA BEZ OSOBY“ (průvodce i řádek tabulky).
 * Hlásí se jen u pojištění osob (ne odpovědnost, majetek, auto…):
 *   · `priradit` — starý zápis limitů bez osoby a v analýze jsou dva lidé
 *     (u jednoho je smlouva jednoznačně jeho),
 *   · `bezRizik` — skutečná smlouva (platba, pojišťovna nebo číslo) bez
 *     jediného rizika: nevíme, koho pojišťuje, a srovnání nemá levou stranu.
 * Nedotčený předvyplněný řádek mlčí.
 * @param {object} row řádek `d.pojistky[]`
 * @param {{ viceOsob?: boolean, lang?: string, prelozit?: (s: string) => string }} [volby]
 *   `prelozit` zůstává kvůli starším volajícím (TL ze stránky) — české texty
 *   jdou přes něj, ostatní jazyky ze slovníku výš.
 * @returns {{ text: string, napoveda: string, kod: "priradit"|"bezRizik" } | null}
 */
export function chybiKohoPojistuje(row, { viceOsob = false, lang = "cs", prelozit = (s) => s } = {}) {
  if (!row || typeof row !== "object" || Array.isArray(row)) return null;
  if (druhBezRizikOsob(row)) return null;
  const rs = rizikaSmlouvy(row);
  let kod = null;
  if (rs.bezOsoby && viceOsob) kod = "priradit";
  else if (!rs.maRizika && vyplnenaSmlouva(row)) kod = "bezRizik";
  if (!kod) return null;
  const [text, napoveda] = textKontroly(kod, lang);
  return lang === "cs" || !TEXTY[kod][lang]
    ? { text: prelozit(text), napoveda: prelozit(napoveda), kod }
    : { text, napoveda, kod };
}

/* ── TEXTY TABULKY RIZIK (pět jazyků) ─────────────────────────────────── */

/* Názvy rizik = názvy karty Pojištění osob (`pojisteniOsob.*` ve slovnících),
 * jen bez stupně u invalidity — „(2.–3. st.)“ je dimenzování plánu, ne název
 * rizika ve stávající smlouvě. Kontrola 14. 10. 2026 (B4): vietnamská tabulka
 * měla invaliditu „Thương tật“ (= úraz) a PN „Mất khả năng lao động“, které
 * zbytek appky čte jako invaliditu → poradce zapsal invaliditu do řádku PN.
 * Shodu hlídá lib/__tests__/rizikaOpravyKontroly2026_10_14.test.js. */
export const TEXTY_RIZIK = Object.freeze({
  cs: {
    nadpis: "Rizika ve smlouvách", popis: "Kdo má vyplněné riziko, je ve smlouvě pojištěný. Platba zůstává na smlouvě.",
    riziko: "Riziko", klient: "Klient", partner: "Partner", mimo: "není v analýze",
    invalidita: "Invalidita", smrt: "Smrt", pn: "Pracovní neschopnost", zavazne: "Závažná onemocnění",
    tnu: "Trvalé následky úrazu", odskodne: "Denní odškodné úrazem", denne: "/den",
    otevrit: "Rizika", zavrit: "Skrýt rizika", zadne: "zatím bez rizik", spolecna: "společná smlouva",
    bezOsoby: "Bez osoby", patri: "Starší zápis bez osoby — patří:", platba: "platba",
    bezDruhu: "U tohoto druhu pojištění se rizika osob nezadávají. Starší zápis zůstává, dokud ho nesmažete.",
    zadnaSmlouva: "Zatím žádná smlouva pojištění osob.",
  },
  sk: {
    nadpis: "Riziká v zmluvách", popis: "Kto má vyplnené riziko, je v zmluve poistený. Platba zostáva na zmluve.",
    riziko: "Riziko", klient: "Klient", partner: "Partner", mimo: "nie je v analýze",
    invalidita: "Invalidita", smrt: "Smrť", pn: "Pracovná neschopnosť", zavazne: "Závažné choroby",
    tnu: "Trvalé následky úrazu", odskodne: "Denné odškodné úrazom", denne: "/deň",
    otevrit: "Riziká", zavrit: "Skryť riziká", zadne: "zatiaľ bez rizík", spolecna: "spoločná zmluva",
    bezOsoby: "Bez osoby", patri: "Starší zápis bez osoby — patrí:", platba: "platba",
    bezDruhu: "Pri tomto druhu poistenia sa riziká osôb nezadávajú. Starší zápis zostáva, kým ho nezmažete.",
    zadnaSmlouva: "Zatiaľ žiadna zmluva poistenia osôb.",
  },
  en: {
    nadpis: "Risks in contracts", popis: "Whoever has a risk filled in is insured by the contract. The payment stays on the contract.",
    riziko: "Risk", klient: "Client", partner: "Partner", mimo: "not in the analysis",
    invalidita: "Disability", smrt: "Death", pn: "Sick leave", zavazne: "Critical illness",
    tnu: "Permanent injury", odskodne: "Daily accident benefit", denne: "/day",
    otevrit: "Risks", zavrit: "Hide risks", zadne: "no risks yet", spolecna: "joint contract",
    bezOsoby: "No person", patri: "Older entry without a person — belongs to:", platba: "payment",
    bezDruhu: "Personal risks are not entered for this type of insurance. The older entry stays until you delete it.",
    zadnaSmlouva: "No personal insurance contract yet.",
  },
  uk: {
    nadpis: "Ризики в договорах", popis: "Хто має заповнений ризик, той застрахований договором. Платіж лишається на договорі.",
    riziko: "Ризик", klient: "Клієнт", partner: "Партнер", mimo: "немає в аналізі",
    invalidita: "Інвалідність", smrt: "Смерть", pn: "Непрацездатність", zavazne: "Критичні захворювання",
    tnu: "Стійкі наслідки травми", odskodne: "Щоденна виплата за травму", denne: "/день",
    otevrit: "Ризики", zavrit: "Сховати ризики", zadne: "поки без ризиків", spolecna: "спільний договір",
    bezOsoby: "Без особи", patri: "Старіший запис без особи — належить:", platba: "платіж",
    bezDruhu: "Для цього виду страхування ризики осіб не вводяться. Старіший запис лишається, доки його не видалите.",
    zadnaSmlouva: "Поки немає договору страхування осіб.",
  },
  vi: {
    nadpis: "Rủi ro trong hợp đồng", popis: "Ai có rủi ro được điền thì được hợp đồng bảo hiểm. Phí đóng vẫn tính theo hợp đồng.",
    riziko: "Rủi ro", klient: "Khách hàng", partner: "Bạn đời", mimo: "không có trong phân tích",
    invalidita: "Mất sức lao động", smrt: "Tử vong", pn: "Nghỉ ốm", zavazne: "Bệnh hiểm nghèo",
    tnu: "Thương tật vĩnh viễn", odskodne: "Trợ cấp tai nạn theo ngày", denne: "/ngày",
    otevrit: "Rủi ro", zavrit: "Ẩn rủi ro", zadne: "chưa có rủi ro", spolecna: "hợp đồng chung",
    bezOsoby: "Chưa gắn người", patri: "Mục nhập cũ chưa gắn người — thuộc về:", platba: "phí",
    bezDruhu: "Loại bảo hiểm này không nhập rủi ro cá nhân. Mục nhập cũ được giữ cho đến khi bạn xóa.",
    zadnaSmlouva: "Chưa có hợp đồng bảo hiểm cá nhân.",
  },
});

/** Texty tabulky rizik v jazyce poradce (neznámý jazyk → čeština). */
export const textyRizik = (lang = "cs") => TEXTY_RIZIK[lang] || TEXTY_RIZIK.cs;
